import React, { useEffect, useRef, useState } from "react";
import useLoadingPresentation, {
  LOADING_SKELETON_DELAY_MS,
  LOADING_SKELETON_MIN_VISIBLE_MS,
  LoadingPresentation
} from "../../../../../shared/hooks/useLoadingPresentation";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import {
  KuikaSkeletonCardGrid,
  KuikaSkeletonDetail,
  KuikaSkeletonForm,
  KuikaSkeletonList,
  KuikaSkeletonMenu,
  KuikaSkeletonRegion,
  KuikaSkeletonStats,
  KuikaSkeletonTable
} from "../../../../ui-kit/kuika-skeleton/kuika-skeleton";
import KuikaTable, { KuikaTableColumn } from "../../../../ui-kit/kuika-table/kuika-table";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";

interface IDomainRow {
  id: string;
  name: string;
  status: string;
}

const DOMAINS: IDomainRow[] = [
  { id: "1", name: "app.acme.com", status: "Active" },
  { id: "2", name: "beta.acme.com", status: "Pending DNS" },
  { id: "3", name: "docs.acme.com", status: "Active" }
];

const DOMAIN_COLUMNS: KuikaTableColumn<IDomainRow>[] = [
  {
    id: "name",
    accessorKey: "name",
    header: (): React.ReactNode => "Domain",
    cell: (row: IDomainRow): React.ReactNode => row.name
  },
  {
    id: "status",
    accessorKey: "status",
    header: (): React.ReactNode => "Status",
    cell: (row: IDomainRow): React.ReactNode => row.status
  }
];

const FAST_LOAD_MS: number = 100;
const SLOW_LOAD_MS: number = 1500;

interface IStageProps {
  width?: number;
  children: React.ReactNode;
}

/** A panel surface (--dark-0), where skeletons really sit; the lab's own paper matches the skeleton surface in dark. */
const Stage: React.FC<IStageProps> = ({ width = 320, children }: IStageProps): JSX.Element => (
  <div style={{ width, padding: 16, border: "1px dashed var(--lab-border)", background: "var(--dark-0)" }}>
    {children}
  </div>
);

/** A fake fetch: `loading` for `ms`, then the rows (or none). `fromEmpty` starts it with no rows on screen. */
const useFakeLoad = (): [boolean, IDomainRow[], (ms: number, rows: IDomainRow[], fromEmpty?: boolean) => void] => {
  const [loading, setLoading] = useState<boolean>(false);
  const [rows, setRows] = useState<IDomainRow[]>([]);
  const timerRef = useRef<number | undefined>(undefined);

  useEffect((): (() => void) => (): void => window.clearTimeout(timerRef.current), []);

  const load = (ms: number, nextRows: IDomainRow[], fromEmpty: boolean = false): void => {
    window.clearTimeout(timerRef.current);
    if (fromEmpty) {
      setRows([]);
    }
    setLoading(true);
    timerRef.current = window.setTimeout((): void => {
      setRows(nextRows);
      setLoading(false);
    }, ms);
  };
  return [loading, rows, load];
};

const TableDemo: React.FC = (): JSX.Element => {
  const [loading, rows, load] = useFakeLoad();
  const [runKey, setRunKey] = useState<number>(0);

  const firstLoad = (ms: number, nextRows: IDomainRow[]): void => {
    // A new table instance, so every run starts from an empty first load.
    setRunKey((key: number): number => key + 1);
    load(ms, nextRows, true);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, width: 520 }}>
      <div style={{ display: "flex", gap: 8 }}>
        <KuikaButton type="secondary" size="sm" onClick={() => firstLoad(FAST_LOAD_MS, DOMAINS)}>
          First load · {FAST_LOAD_MS}ms
        </KuikaButton>
        <KuikaButton type="secondary" size="sm" onClick={() => firstLoad(SLOW_LOAD_MS, DOMAINS)}>
          First load · {SLOW_LOAD_MS}ms
        </KuikaButton>
        <KuikaButton
          type="secondary"
          size="sm"
          disabled={rows.length === 0}
          onClick={() => load(SLOW_LOAD_MS, DOMAINS)}
        >
          Reload with rows
        </KuikaButton>
        <KuikaButton type="secondary" size="sm" onClick={() => firstLoad(SLOW_LOAD_MS, [])}>
          Load an empty list
        </KuikaButton>
      </div>
      <KuikaTable<IDomainRow>
        key={runKey}
        data={rows}
        columns={DOMAIN_COLUMNS}
        rowKey={(row: IDomainRow): string => row.id}
        loading={loading}
        fitContentHeight
        enablePagination={false}
        emptyTitle="No domains yet"
      />
    </div>
  );
};

const GateDemo: React.FC = (): JSX.Element => {
  const [loading, rows, load] = useFakeLoad();
  const presentation: LoadingPresentation = useLoadingPresentation({ isLoading: loading, hasData: rows.length > 0 });

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <KuikaButton type="secondary" size="sm" onClick={() => load(FAST_LOAD_MS, [], true)}>
        {FAST_LOAD_MS}ms, no data
      </KuikaButton>
      <KuikaButton type="secondary" size="sm" onClick={() => load(SLOW_LOAD_MS, [], true)}>
        {SLOW_LOAD_MS}ms, no data
      </KuikaButton>
      <KuikaButton type="secondary" size="sm" onClick={() => load(SLOW_LOAD_MS, DOMAINS, true)}>
        {SLOW_LOAD_MS}ms, then data
      </KuikaButton>
      <code style={{ minWidth: 90 }}>{presentation}</code>
    </div>
  );
};

export const SkeletonStory: React.FC = (): JSX.Element => (
  <ShowcaseFrame
    title="Skeleton"
    componentName="KuikaSkeleton · KuikaSkeletonRegion · compositions"
    sourcePath="ui-kit/kuika-skeleton"
    description={`One skeleton language: the KuikaSkeleton surface and sheen (a still pulse under reduced motion), composed into the shapes the designer loads. Only for content whose shape is known and that comes over the network, and only while the region has none of its own data — a reload keeps the data on screen. The skeleton is drawn after ${LOADING_SKELETON_DELAY_MS}ms of waiting and stays at least ${LOADING_SKELETON_MIN_VISIBLE_MS}ms. KuikaSkeletonRegion owns the announcement: busy region, a status line, the shapes hidden from assistive tech.`}
  >
    <SpecRow label="List" caption="icon + one or two lines; rows at --row-h-{density}" align="start" inline>
      <Stage>
        <KuikaSkeletonRegion>
          <KuikaSkeletonList rows={4} />
        </KuikaSkeletonRegion>
      </Stage>
      <Stage>
        <KuikaSkeletonRegion>
          <KuikaSkeletonList rows={3} density="comfortable" lines={2} />
        </KuikaSkeletonRegion>
      </Stage>
      <Stage>
        <KuikaSkeletonRegion>
          <KuikaSkeletonList rows={5} density="compact" withIcon={false} />
        </KuikaSkeletonRegion>
      </Stage>
    </SpecRow>
    <SpecRow label="Card grid" align="start">
      <Stage width={720}>
        <KuikaSkeletonRegion>
          <KuikaSkeletonCardGrid count={6} />
        </KuikaSkeletonRegion>
      </Stage>
    </SpecRow>
    <SpecRow
      label="Form · detail · menu"
      caption="fields at --control-h-md; menu rows at --row-h-compact"
      align="start"
      inline
    >
      <Stage>
        <KuikaSkeletonRegion>
          <KuikaSkeletonForm fields={3} />
        </KuikaSkeletonRegion>
      </Stage>
      <Stage>
        <KuikaSkeletonRegion>
          <KuikaSkeletonDetail rows={4} />
        </KuikaSkeletonRegion>
      </Stage>
      <Stage width={220}>
        <KuikaSkeletonRegion>
          <KuikaSkeletonMenu rows={5} withIcon />
        </KuikaSkeletonRegion>
      </Stage>
    </SpecRow>
    <SpecRow
      label="List + detail · announce={false}"
      caption="two regions loading side by side say Loading once: the detail region draws its shapes without aria-busy or a status line"
      align="start"
      inline
    >
      <Stage>
        <KuikaSkeletonRegion label="Loading data types">
          <KuikaSkeletonList rows={4} density="compact" />
        </KuikaSkeletonRegion>
      </Stage>
      <Stage>
        <KuikaSkeletonRegion announce={false}>
          <KuikaSkeletonForm fields={3} />
        </KuikaSkeletonRegion>
      </Stage>
    </SpecRow>
    <SpecRow label="Stat tiles" align="start">
      <Stage width={720}>
        <KuikaSkeletonRegion>
          <KuikaSkeletonStats count={4} />
        </KuikaSkeletonRegion>
      </Stage>
    </SpecRow>
    <SpecRow
      label="KuikaSkeletonTable"
      caption="bare header + rows, for tables that are not a KuikaTable"
      align="start"
    >
      <Stage>
        <KuikaSkeletonRegion>
          <KuikaSkeletonTable rows={3} rowHeight={28} header />
        </KuikaSkeletonRegion>
      </Stage>
    </SpecRow>
    <SpecRow
      label="KuikaTable loading"
      caption="skeleton rows only until the first rows arrive; a quick load never shows them; a reload keeps the rows under the spinner"
      align="start"
    >
      <TableDemo />
    </SpecRow>
    <SpecRow
      label="useLoadingPresentation"
      caption="pending → skeleton → content, or refreshing with data"
      align="start"
    >
      <GateDemo />
    </SpecRow>
  </ShowcaseFrame>
);
